import { ReactNode, useEffect, useRef, useState } from 'react';
import { Col, Form, Row } from 'antd';
import FormTitle from '../auth/FormTitle';
import { StyledModal } from '../jobs/UnpublishJobModal';
import FormItem from '../form/FormItem';
import Button from '../../UI/buttons/Button';
import SelectInput from '../../UI/inputs/Select';
import { ReactComponent as EmailIcon } from '../../icons/info-email.svg';
import styled from 'styled-components';
import NewSearchInput from '../../UI/inputs/NewSearchInput';
import RecruiterSelectTable from './RecruiterSelectTable';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import Loader from '../../UI/Loader';
import RecruiterManagementTagColors from '../recruitment_management/RecruiterManagementTagColors';
import { TEST_UI_KEYS, withTestUIWrapper } from '../helper/TestUIWrapper';
import BroadcastMessageModal from '../jobs/BroadcastMessageModal';
import ManageTagsModal from '../jobs/ManageTagsModal';
import { message } from 'antd';
import { PlusOutlined, SwapOutlined, MessageOutlined, TagOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { Recruiter, Specialization, Job } from '../../types';
import { getSpecializationList } from '../../api/specialization';
import AddCompanyRecruiterForm from './AddCompanyRecruiterForm';
import {
  getRecrutersSelectedForJob,
  getPreviouslySelectedRecruiters,
} from '../../api/recruiter';
import { getJob, updateJobStatus, setRecruiterTarget } from '../../api/jobs';
import RecruiterManagementBoardFooter from './RecruiterManagementBoardFooter';
import { useAppSelector } from '../../store/hooks';
import SetRecruiterTargetModal from './SetRecruiterTargetModal';
import TargetHistoryModal from './TargetHistoryModal';

const NoResultText = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.33;
  letter-spacing: 0.96px;
  text-align: center;
  color: #061c2e;
  padding: 1rem;
`;

const StyledEmailIcon = styled(EmailIcon)`
  width: 20px;
  height: 20px;
  margin-right: 0;
  stroke-width: 1.5;
  & path {
    stroke: ${({ theme }) => theme.primaryColor};
    &:disabled,
    &.disabled {
      stroke: #aebeca;
    }
  }
`;

const EmailInviteBtn = styled(Button)`
  box-sizing: border-box;
  height: 40px;
  &:disabled,
  &.disabled {
    height: 40px;
    cursor: not-allowed;
    background: #fff;
    color: #aebeca;
    border-color: #aebeca;
    &:hover {
      background: #dde7ee;
      color: #ffffff;
      border-color: #dde7ee;
      & path {
        transition: all 0.3s;
        stroke: #fff;
      }
    }
    & path {
      stroke: #aebeca;
    }
  }
`;

const CustomFormTitle = styled(FormTitle)`
  font-size: 1.5rem;
`;

export const FormDescription = styled.p`
  font-size: 14px;
  line-height: 1.33;
  color: #324759;
  margin-bottom: 1.2rem;
`;

export type SortOrder = 'ASC' | 'DESC';
export type SortingType = 'RATING' | 'NAME' | 'TYPE';

type RecruiterManagementBoardProps = {
  selectedJob: string | null;
  selectedJobSpecialization?: { id: string; name: string } | null;
  fromJobCreation: boolean;
  topBar?: () => ReactNode,
  onComplete: () => void;
  onConfirm?: (publish: boolean) => Promise<void>;
}

const RecruiterManagementBoardNative = ({
  selectedJob,
  fromJobCreation,
  topBar,
  onComplete,
  selectedJobSpecialization,
  onConfirm,
}: RecruiterManagementBoardProps) => {
  const loader = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [loadingJob, setLoadingJob] = useState(true);
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [allDomains, setAllDomains] = useState<Specialization[]>([]);
  const [isAddRecruiterFormVisible, setIsAddRecruiterFormVisible] =
    useState<boolean>(false);
  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [allSelectedInThisSession, setAllSelectedInThisSession] =
    useState<boolean>(false);
  const [recruitersSelectedInThisSession, setRecruitersSelectedInThisSession] =
    useState<string[]>([]);
  const [
    totalNumberOfAvailableRecruiters,
    setTotalNumberOfAvailableRecruiters,
  ] = useState<number>(0);
  const [totalNumberOfSelectedRecruiters, setTotalNumberOfSelectedRecruiters] =
    useState<number>(0);
  const [isBroadcastModalVisible, setIsBroadcastModalVisible] = useState(false);
  const [isManageTagsDropdownVisible, setIsManageTagsDropdownVisible] = useState(false);
  const [jobStatusLoading, setJobStatusLoading] = useState<boolean>(false);

  // <<< State for managing the Set Target Modal >>>
  const [isSetTargetModalVisible, setIsSetTargetModalVisible] = useState<boolean>(false);
  const [editingRecruiter, setEditingRecruiter] = useState<Recruiter | null>(null);
  // <<< End State >>>

  // <<< State for managing the Target History Modal >>>
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState<boolean>(false);
  const [viewingAssignmentId, setViewingAssignmentId] = useState<string | null>(null);
  // <<< End State >>>

  const { t } = useTranslation();
  const user = useAppSelector(state => state.user.user);
  const companyName = user?.company?.name;
  let [currentSortingType, setCurrentSortingType] =
    useState<SortingType>('TYPE');
  let [currentSortingOrder, setCurrentSortingOrder] =
    useState<SortOrder>('DESC');
  let [sortQueryString, setSortQueryString] = useState('-own_recruiter');

  const setRecruiterIsSelectedField = (
    rec: Recruiter,
    new_is_selected_val: boolean,
  ) => {
    let updatedRecs = [...recruiters];

    const indexToUpdate = updatedRecs.findIndex(
      (recruiter) => recruiter.id === rec.id,
    );
    // Create a copy of the recruiter object you want to update
    const updatedRecruiter = { ...updatedRecs[indexToUpdate] };

    // Update the desired field of the recruiter object
    updatedRecruiter.is_selected = new_is_selected_val;
    if (new_is_selected_val) {
      setRecruitersSelectedInThisSession((prevState) => [
        ...prevState,
        updatedRecruiter.id,
      ]);
    } else {
      setRecruitersSelectedInThisSession([
        ...recruitersSelectedInThisSession.filter(
          (rec: string) => rec !== updatedRecruiter.id,
        ),
      ]);
    }

    // Replace the old recruiter object with the updated one
    updatedRecs[indexToUpdate] = updatedRecruiter;

    if (new_is_selected_val) {
      setTotalNumberOfSelectedRecruiters((prevState) => prevState + 1);
    } else {
      setTotalNumberOfSelectedRecruiters((prevState) => prevState - 1);
    }

    // Update the state
    setRecruiters(updatedRecs);
  };

  const handleSelectAll = (isChecked: boolean) => {
    setAllSelected(isChecked);

    if (!isChecked) {
      setRecruitersSelectedInThisSession([]);
    }
    if (isChecked && !allSelectedInThisSession) {
      setAllSelectedInThisSession(true);
    }
    if (isChecked) {
      setTotalNumberOfSelectedRecruiters(totalNumberOfAvailableRecruiters);
    } else {
      setTotalNumberOfSelectedRecruiters(0);
    }
    setRecruiters(
      recruiters.map((rec) => {
        rec.is_selected = isChecked;
        return rec;
      }),
    );
  };

  useEffect(() => {
    if (selectedJob) {
      setLoadingJob(true);
      getJob(selectedJob)
        .then((res) => {
          if (res?.data) {
            setCurrentJob(res.data);
          }
        })
        .catch((err) => {
          console.error("Error fetching job data:", err);
          // Handle error appropriately, e.g., show a message
        })
        .finally(() => {
          setLoadingJob(false);
        });
    }
  }, [selectedJob]);

  useEffect(() => {
    setLoading(true);
    getSpecializationList().then((res) => {
      setAllDomains(
        res.data.map((x: any) => ({
          ...x,
          color: RecruiterManagementTagColors[Math.floor(Math.random() * RecruiterManagementTagColors.length)],
        })),
      );
    });
    getRecrutersSelectedForJob({
      query: `${companyName}-${selectedJob}&sort=${sortQueryString}`,
      job_id: selectedJob,
    }).then((res: any) => {
      setTotalNumberOfSelectedRecruiters(
        res.data.results?.[0]?.total_previously_selected_recs,
      );
      setRecruiters(res.data.results);
      setNext(res.data.pagination.next);
      setTotalNumberOfAvailableRecruiters(res.data.pagination.count);
    });
    getPreviouslySelectedRecruiters(`${companyName}-${selectedJob}`).then(
      (res: any) => {
        setRecruitersSelectedInThisSession(res.data.results);
      },
    );
    setLoading(false);
  }, [companyName, selectedJob, sortQueryString]);

  useEffect(() => {
    switch (currentSortingType) {
      case 'NAME':
        if (currentSortingOrder === 'ASC')
          setSortQueryString('user__first_name');
        if (currentSortingOrder === 'DESC')
          setSortQueryString('-user__first_name');
        break;
      case 'TYPE':
        if (currentSortingOrder === 'ASC') setSortQueryString('own_recruiter');
        if (currentSortingOrder === 'DESC')
          setSortQueryString('-own_recruiter');
        break;
      case 'RATING':
        if (currentSortingOrder === 'ASC')
          setSortQueryString('-overall_rating');
        if (currentSortingOrder === 'DESC')
          setSortQueryString('overall_rating');
        break;
    }
    // eslint-disable-next-line
  }, [currentSortingType, currentSortingOrder]);

  let onFilter = (_: any, allValues: any) => {
    let querySearch: any[] = [];

    allValues.forEach((elem: any) => {
      if (elem['name'][0] === 'rating' || elem['name'][0] === 'category')
        return;
      querySearch.push({ param: elem['name'][0], value: elem.value });
    });

    let queryString = querySearch
      .filter((elem) => elem.value !== undefined)
      .map((elem) => {
        if (Array.isArray(elem.value)) {
          elem.value = elem.value.join(',');
          return elem;
        }
        return elem;
      })
      .map((elem) => `${elem.param}=${elem.value}`)
      .join('&');

    queryString = queryString + `&sort=${sortQueryString}`;
    setLoading(true);
    getRecrutersSelectedForJob({
      query: `${companyName}-${selectedJob}&${queryString}`,
      job_id: selectedJob,
    }).then((res) => {
      let newRecs = res.data.results;
      if (allSelected) {
        newRecs = newRecs.map((rec: Recruiter) => {
          rec.is_selected = true;
          return rec;
        });
      }
      if (allSelectedInThisSession && !allSelected) {
        newRecs = newRecs.map((rec: Recruiter) => {
          rec.is_selected = false;
          return rec;
        });
      }

      setRecruiters(
        newRecs.map((rec: Recruiter) => {
          if (recruitersSelectedInThisSession.includes(rec.id)) {
            rec.is_selected = true;
            return rec;
          }
          return rec;
        }),
      );
      setNext(res.data.pagination.next);
    });
    setLoading(false);
  };

  onFilter = debounce(onFilter, 300, { trailing: true });

  const handleTableScroll = (e: any) => {
    if (next === null || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Check if the scroll position is at the bottom
    // -1 fixed a bug not sure why, don't touch unless very neccessery
    if (scrollTop + clientHeight >= scrollHeight - 1) {
      setLoading(true);
      getRecrutersSelectedForJob({
        query: `${companyName}-${selectedJob}&cursor=${next}&sort=${sortQueryString}`,
        job_id: selectedJob,
      })
        .then((res) => {
          let newRecs = res.data.results;
          if (allSelected) {
            newRecs = newRecs.map((rec: Recruiter) => {
              rec.is_selected = true;
              return rec;
            });
          }
          if (allSelectedInThisSession && !allSelected) {
            newRecs = newRecs.map((rec: Recruiter) => {
              rec.is_selected = false;
              return rec;
            });
          }
          if (recruitersSelectedInThisSession.length) {
            newRecs = newRecs.map((rec: Recruiter) => {
              if (recruitersSelectedInThisSession.includes(rec.id)) {
                rec.is_selected = true;
                return rec;
              }
              return rec;
            });
          }
          setRecruiters((prevState: Recruiter[]) => [...prevState, ...newRecs]);
          setNext(res.data.pagination.next);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleInviteRecruiterBtnClick = () => {
    setIsAddRecruiterFormVisible(true);
  };

  const handleBroadcastClick = () => {
    setIsBroadcastModalVisible(true);
  };

  const handleCloseBroadcastModal = () => {
    setIsBroadcastModalVisible(false);
  };

  const handleTagsUpdate = (updatedJob: Job) => {
    setCurrentJob(updatedJob);
  };

  const handleManageTagsClick = () => {
    setIsManageTagsDropdownVisible(true);
  };

  const handleCloseManageTagsModal = () => {
    setIsManageTagsDropdownVisible(false);
  };

  const handleToggleHoldClick = async () => {
    if (!currentJob) return;
    setJobStatusLoading(true);
    const newStatus = currentJob.status === 'on_hold' ? 'published' : 'on_hold';
    try {
      const response = await updateJobStatus(currentJob.id, newStatus);
      // Update local state immediately for better UX
      setCurrentJob(response.data);
      message.success(
        newStatus === 'on_hold'
          ? t('JOB_STATUS_SET_ON_HOLD_SUCCESS')
          : t('JOB_STATUS_SET_PUBLISHED_SUCCESS'),
      );
    } catch (error) {
      message.error(t('ERROR_UPDATING_JOB_STATUS'));
      console.error('Error updating job status:', error);
    } finally {
      setJobStatusLoading(false);
    }
  };

  // Section heading variant with smaller font and no bottom margin
  const SectionTitle = styled(FormTitle)`
    font-size: 1rem;
    margin-bottom: 0;
    margin-right: 8px;
  `;

  // <<< Function to open Set Target Modal >>>
  const handleEditTargetClick = (recruiter: Recruiter) => {
    setEditingRecruiter(recruiter);
    setIsSetTargetModalVisible(true);
  };
  // <<< End Function >>>

  // <<< Function to close Set Target Modal >>>
  const handleCloseSetTargetModal = () => {
    setIsSetTargetModalVisible(false);
    setEditingRecruiter(null);
  };
  // <<< End Function >>>

  // <<< Function to open Target History Modal >>>
  const handleViewHistoryClick = (assignmentId: string | null | undefined) => {
    if (assignmentId) {
      setViewingAssignmentId(assignmentId);
      setIsHistoryModalVisible(true);
    }
  };
  // <<< End Function >>>

  // <<< Function to close Target History Modal >>>
  const handleCloseHistoryModal = () => {
    setIsHistoryModalVisible(false);
    setViewingAssignmentId(null);
  };
  // <<< End Function >>>

  // <<< Update save handler to call API >>>
  const handleSaveTarget = async (targetType: string, targetValue: number) => {
    if (!editingRecruiter || !selectedJob) {
      message.error(t('ERROR_MISSING_INFO', 'Missing recruiter or job information.'));
      return;
    }

    // Ensure user id exists before proceeding (should always exist on Recruiter type)
    if (!editingRecruiter.id) {
      message.error(t('ERROR_MISSING_USER_ID', 'Recruiter user ID is missing. Cannot set target.'));
      return;
    }

    try {
      await setRecruiterTarget({
        jobId: selectedJob,
        userId: editingRecruiter.id, // Changed from recruiterProfileId to userId using recruiter.id
        targetType,
        targetValue,
      });
      message.success(t('TARGET_SET_SUCCESS', 'Target updated successfully!'));

      // Refresh the recruiter list to show the updated target
      // Find the edited recruiter and update their target fields locally for immediate feedback
      setRecruiters(prevRecruiters =>
        prevRecruiters.map(rec =>
          rec.id === editingRecruiter.id
            ? { ...rec, current_target_type: targetType as ('hires' | 'introductions'), current_target_value: targetValue }
            : rec
        )
      );

      handleCloseSetTargetModal(); // Close modal on success
    } catch (error) {
      message.error(t('TARGET_SET_ERROR', 'Failed to update target.'));
      // Keep modal open on error for user to retry or cancel
    }
  };
  // <<< End Update >>>

  // Handler to update recruiter permission state
  const handleUpdateRecruiterPermission = (recruiterToUpdate: Recruiter, canAnswer: boolean) => {
    setRecruiters(prevRecruiters =>
      prevRecruiters.map(rec =>
        rec.id === recruiterToUpdate.id
          ? { ...rec, can_answer_extension_fields: canAnswer }
          : rec
      )
    );
  };

  return (
    <>
      {topBar && topBar()}
      <Form
        scrollToFirstError={true}
        layout="horizontal"
        onFieldsChange={onFilter}
      >
        <CustomFormTitle text={t('Select talent suppliers')} marginBottom="0.2em" />
        <Row justify="start" align="middle" gutter={10}>
          <Col flex="auto">
            <FormItem name="search_term">
              <NewSearchInput placeholder={t('RECRUITER_MANAGEMENT_FILTER_SEARCH_PLACEHOLDER', 'Name, company, title, email...')} />
            </FormItem>
          </Col>
          {(!user?.permissions || Object.keys(user.permissions).length === 0 || user.permissions.create_suppliers) && (
            <Col>
              <FormItem>
                <EmailInviteBtn
                  size="middle"
                  onClick={handleInviteRecruiterBtnClick}
                  disabled={isAddRecruiterFormVisible}
                >
                  <StyledEmailIcon />
                  {t('ADD_AGENCIES_BTN', 'Add Agencies')}
                </EmailInviteBtn>
              </FormItem>
            </Col>
          )}
        </Row>
        <AddCompanyRecruiterForm
          isVisible={isAddRecruiterFormVisible}
          setIsVisible={setIsAddRecruiterFormVisible}
        />
      </Form>

      <Row justify="space-between" align="middle" style={{ marginBottom: '1rem', marginTop: '1rem' }}>
        {/* Agency Lists Section */}
        <Col>
          <Row align="middle" gutter={8}>
            <Col>
              <SectionTitle text={t('AGENCY_LISTS_TITLE', 'Agency Lists:')} />
            </Col>
            <Col flex="auto" style={{ minWidth: '200px' }}>
              <SelectInput
                placeholder={t('AGENCY_LISTS_SELECT_PLACEHOLDER', 'Type or select existing')}
                disabled
                size="small" // Match screenshot button size
              />
            </Col>
            <Col>
              <Button size="small" icon={<PlusOutlined />} disabled>
                {t('ADD_BTN', 'Add')}
              </Button>
            </Col>
            <Col>
              <Button size="small" icon={<SwapOutlined />} disabled>
                {t('REPLACE_BTN', 'Replace')}
              </Button>
            </Col>
          </Row>
        </Col>

        {/* Tools Section */}
        <Col>
          <Row align="middle" gutter={8}>
            <Col>
              <SectionTitle text={t('TOOLS_TITLE', 'Tools:')} />
            </Col>
            {!fromJobCreation && !loadingJob && currentJob && (
              <>
                <Col>
                  <Button type="default" icon={<MessageOutlined />} onClick={handleBroadcastClick} size="small">
                    {t('BROADCAST_MESSAGE_BTN', 'Broadcast Message')}
                  </Button>
                </Col>
                <Col>
                  <Button type="default" icon={<TagOutlined />} onClick={handleManageTagsClick} size="small">
                    {t('MANAGE_TAGS_BTN_COUNT', `Manage tags (${currentJob?.tags?.length || 0})`, { count: currentJob?.tags?.length || 0 })}
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="default"
                    icon={<PauseCircleOutlined />}
                    onClick={handleToggleHoldClick}
                    danger={currentJob.status !== 'on_hold'}
                    size="small"
                    loading={jobStatusLoading}
                    disabled={jobStatusLoading}
                  >
                    {currentJob.status === 'on_hold'
                      ? t('REPUBLISH_JOB_BTN', 'Republish Job')
                      : t('JOB_ON_HOLD_BTN', 'Job on Hold')}
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </Col>
      </Row>

      <Loader spinning={loading}>
        {!loading && recruiters.length === 0 ? (
          <NoResultText>{t('NO_RESULT')}</NoResultText>
        ) : (
          <RecruiterSelectTable
            recruiters={recruiters}
            domains={allDomains}
            selectedJobSpecialization={selectedJobSpecialization}
            setRecruiterIsSelectedField={setRecruiterIsSelectedField}
            handleTableScroll={handleTableScroll}
            handleSelectAll={handleSelectAll}
            setSortType={setCurrentSortingType}
            setSortOrder={setCurrentSortingOrder}
            currentSortOrder={currentSortingOrder}
            currentSortType={currentSortingType}
            onEditTargetClick={handleEditTargetClick}
            onViewHistoryClick={handleViewHistoryClick}
            jobId={selectedJob}
            updateRecruiterPermissionField={handleUpdateRecruiterPermission}
          />
        )}
        <div className="loader" ref={loader} />
      </Loader>

      {selectedJob && (
        <BroadcastMessageModal
          jobId={selectedJob}
          isVisible={isBroadcastModalVisible}
          onClose={handleCloseBroadcastModal}
        />
      )}

      {currentJob && (
        <ManageTagsModal
          job={currentJob}
          isVisible={isManageTagsDropdownVisible}
          onClose={handleCloseManageTagsModal}
          onTagsUpdate={handleTagsUpdate}
        />
      )}

      {/* <<< Render Set Target Modal instance >>> */}
      <SetRecruiterTargetModal
        isVisible={isSetTargetModalVisible}
        recruiter={editingRecruiter}
        onSave={handleSaveTarget}
        onClose={handleCloseSetTargetModal}
      />

      {/* <<< Render Target History Modal instance >>> */}
      <TargetHistoryModal
        isVisible={isHistoryModalVisible}
        jobId={selectedJob}
        assignmentId={viewingAssignmentId}
        onClose={handleCloseHistoryModal}
      />

      <RecruiterManagementBoardFooter
        onComplete={onComplete}
        fromJobCreation={fromJobCreation}
        companyName={companyName}
        selectedJob={selectedJob}
        allRows={recruiters.map((rec) => rec.id)}
        selectedRows={recruiters
          .filter((rec) => rec.is_selected)
          .map((rec) => rec.id)}
        recruitersSelectedInThisSession={recruitersSelectedInThisSession}
        highThreshold={35}
        numOfSelectedRecrutiers={totalNumberOfSelectedRecruiters}
        allSelected={allSelected}
        allSelectedInThisSession={allSelectedInThisSession}
        onConfirm={onConfirm}
      />
    </>
  );
}

export const RecruiterManagementBoard = withTestUIWrapper(RecruiterManagementBoardNative, ({ selectedJob }) => ({
  testKey: TEST_UI_KEYS.SelectTalentSuppliers,
  params: {
    jobId: selectedJob,
  },
}));

type RecruiterManagementBoardModalProps = {
  isVisible: boolean;
  onComplete: () => void;
  selectedJob: string | null;
  fromJobCreation: boolean;
  selectedJobSpecialization?: { id: string; name: string } | null;
}

export function RecruiterManagementBoardModal({
  isVisible,
  onComplete,
  selectedJob,
  fromJobCreation,
  selectedJobSpecialization,
}: RecruiterManagementBoardModalProps) {
  return (
    <StyledModal
      style={{ top: '20px' }}
      destroyOnClose={true}
      footer={null}
      visible={isVisible}
      width={1100}
      onCancel={() => onComplete()}
    >
      <RecruiterManagementBoard
        onComplete={onComplete}
        fromJobCreation={fromJobCreation}
        selectedJob={selectedJob}
        selectedJobSpecialization={selectedJobSpecialization}
      />
    </StyledModal>
  );
}

export default RecruiterManagementBoard;